import { useEffect, useRef, useState } from "react";
import type { ThreadMessageLike } from "@assistant-ui/react";
import type { CuadraChatClient } from "../lib/cuadraChatClient";
import {
  activeStreamingChatIds,
  messageIdToChatIdMap,
} from "../adapters/threadListAdapter";
import {
  convertFromCuadraMessage,
  injectArtifactMarkers,
  isSupportedRole,
} from "../adapters/messageConverter";
import { artifactStore } from "../adapters/artifactStore";
import { isDocumentArtifactGenerating } from "../types/artifact";
import { chatErrorStore } from "../adapters/chatErrorStore";

interface CommittedState {
  key: string;
  messages: ThreadMessageLike[] | undefined;
}

export interface UseSingleChatLoaderResult {
  committed: CommittedState;
  isFirstLoad: boolean;
}

/**
 * Manages the "deferred swap" pattern for single-chat mode.
 *
 * Fetches thread messages when `serverThreadId` changes and atomically
 * swaps the committed state (key + messages) so the UI transitions in a
 * single render frame.  A spinner state (`isFirstLoad`) is only true
 * during the very first load when there is no prior content to display.
 *
 * Also detects model changes and resets to a new-chat state.
 */
export function useSingleChatLoader(opts: {
  client: CuadraChatClient;
  /** Resolved server chat id (URL, bg reload, or runtime `onChatCreated`). */
  serverThreadId?: string;
  modelId?: string | null;
  onChatModelResolved?: (modelId: string) => void;
  enableArtifacts?: boolean;
  /** Increment to force a re-fetch of the current thread (e.g. after background generation). */
  reloadKey?: number;
  /** Server chat id paired with `reloadKey` (background-generation reload). */
  reloadChatId?: string | null;
}): UseSingleChatLoaderResult {
  const {
    client,
    serverThreadId,
    modelId,
    onChatModelResolved,
    enableArtifacts,
    reloadKey,
    reloadChatId,
  } = opts;

  const [committed, setCommitted] = useState<CommittedState>({
    key: serverThreadId ?? "new",
    messages: undefined,
  });
  const [isFirstLoad, setIsFirstLoad] = useState(!!serverThreadId);

  const onChatModelResolvedRef = useRef(onChatModelResolved);
  onChatModelResolvedRef.current = onChatModelResolved;

  // Model change detection
  const modelInitRef = useRef(false);
  const prevModelIdRef = useRef(modelId);

  useEffect(() => {
    if (!modelInitRef.current) {
      modelInitRef.current = true;
      prevModelIdRef.current = modelId;
      return;
    }

    if (prevModelIdRef.current !== modelId) {
      setCommitted({ key: `new:${Date.now()}`, messages: undefined });
      setIsFirstLoad(false);
    }

    prevModelIdRef.current = modelId;
  }, [modelId]);

  const prevServerThreadIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const prevId = prevServerThreadIdRef.current;
    prevServerThreadIdRef.current = serverThreadId;

    if (!serverThreadId) {
      const snap = artifactStore.getSnapshot();
      const keepArtifactChrome =
        isDocumentArtifactGenerating(snap.artifactUiPhase) ||
        (snap.panelOpen && !!snap.activeArtifactId);
      if (!keepArtifactChrome) {
        artifactStore.closePanel();
        setCommitted({ key: "new", messages: undefined });
        setIsFirstLoad(false);
      }
      return;
    }

    // Close the artifact panel when switching between DIFFERENT chats so
    // users don't see a stale panel during the async load.
    //
    // Skip closing when:
    //  - prevId is undefined → the chat is transitioning from "new" to its
    //    server-assigned id (e.g. background generation completed and
    //    reloadChatId was set).  This is the SAME chat, not a switch.
    //  - The new chat has an active stream → the adapter is populating
    //    artifacts via onArtifactStart/Delta and closing would cause flicker
    //    (e.g. dashboard transitions from new-chat to chat/{id} mid-stream).
    if (
      serverThreadId !== prevId &&
      !!prevId &&
      !activeStreamingChatIds.has(serverThreadId)
    ) {
      artifactStore.closePanel();
    }

    if (activeStreamingChatIds.has(serverThreadId)) {
      return;
    }

    let cancelled = false;

    async function loadThread() {
      try {
        const [chat, artifactResponse] = await Promise.all([
          client.getChat(serverThreadId!),
          enableArtifacts
            ? client
                .listArtifacts(serverThreadId!)
                .catch(() => ({ items: [], totalCount: 0 }))
            : Promise.resolve({ items: [], totalCount: 0 }),
        ]);
        if (cancelled) return;

        if (artifactResponse.items.length > 0) {
          artifactStore.loadArtifacts(artifactResponse.items);

          // If the panel is still open from the pending state (background
          // generation just completed), activate the most recent artifact
          // so the panel transitions from loading to showing content.
          const snap = artifactStore.getSnapshot();
          if (snap.panelOpen && !artifactStore.getActiveArtifact()) {
            const latest =
              artifactResponse.items[artifactResponse.items.length - 1];
            artifactStore.setActiveArtifact(latest.id);
          }
        }

        let messages: ThreadMessageLike[] | undefined;
        if (chat.messages && chat.messages.length > 0) {
          const resolveUrl = (p: string) => client.getUrl(p);
          const supported = chat.messages.filter((msg) =>
            isSupportedRole(msg.role),
          );
          const withMarkers = injectArtifactMarkers(
            supported,
            artifactResponse.items,
          );
          messages = withMarkers.map((msg) =>
            convertFromCuadraMessage(msg, resolveUrl, chat.id),
          );

          for (const msg of chat.messages) {
            messageIdToChatIdMap.set(msg.id, chat.id);
          }
        }

        if (chat.status === "error" && chat.lastError) {
          chatErrorStore.set({
            chatId: chat.id,
            lastError: chat.lastError,
            lastErrorAt: chat.lastErrorAt ?? new Date().toISOString(),
          });
        } else {
          chatErrorStore.clear();
        }

        if (chat.modelId) {
          prevModelIdRef.current = chat.modelId;
          onChatModelResolvedRef.current?.(chat.modelId);
        }

        const reloadSuffix = reloadKey ? `:reload:${reloadKey}` : "";
        setCommitted({
          key: `${serverThreadId!}${reloadSuffix}`,
          messages,
        });
        setIsFirstLoad(false);
      } catch (_err) {
        if (!cancelled) {
          const reloadSuffix = reloadKey ? `:reload:${reloadKey}` : "";
          setCommitted({
            key: `${serverThreadId!}${reloadSuffix}`,
            messages: undefined,
          });
          setIsFirstLoad(false);
        }
      }
    }

    loadThread();
    return () => {
      cancelled = true;
    };
  }, [client, serverThreadId, enableArtifacts, reloadKey, reloadChatId]);

  return { committed, isFirstLoad };
}
